"use client";

/**
 * Unified account hook for swap operations.
 *
 * Routes to widget hooks (BridgeWalletAdapter) when WidgetWalletProvider is mounted,
 * otherwise falls back to Para hooks for the main site.
 *
 * SwapTokensForm and other swap components should use this instead of
 * useParaAccount directly to support both main site and widget contexts.
 */

import { useMemo } from "react";
import { Connection } from "@solana/web3.js";
import { useWidgetWalletContext } from "@/contexts/widget-wallet-context";
import { useParaAccount } from "@/hooks/use-appkit-account";

/** Return type matches useParaAccount exactly */
export interface SwapAccountResult {
  address: string | undefined;
  caipAddress: string | undefined;
  isConnected: boolean;
  isExternalSolana: boolean | undefined;
  isExternalEvm: boolean | undefined;
  connection: Connection;
  walletType: "SOLANA" | "EVM" | undefined;
}

export function useSwapAccount(): SwapAccountResult {
  // Check if we're in widget context
  const widgetContext = useWidgetWalletContext();

  // Always call Para hook (React hook rules - can't conditionally call)
  const paraAccount = useParaAccount();

  return useMemo(() => {
    // Widget context takes priority when mounted
    if (widgetContext) {
      return widgetContext.accountData;
    }

    // Fall back to Para hooks on main site
    return paraAccount;
  }, [widgetContext, paraAccount]);
}
